import React, { useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import AddCropInfo from '../../views/AddCropInfo';
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import api from '../../api/api';
import { useNavigate } from "react-router-dom";
import { getUserId } from "../../utils/auth";

const AddCropInfoWrapper = ({ wizardData, completeStep, onPrevious, onNext, resetAllData, isWizardMode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cropData, setCropData] = React.useState({});
  const [listCrop, setListCrop] = React.useState([]);

  // Initialiser les données à partir du wizardData uniquement au montage
  useEffect(() => {
    if (wizardData?.step3?.data) {
      setCropData(wizardData.step3.data);
    }
  }, []);

  useEffect(() => {
    const wasRecentlySubmitted = localStorage.getItem('cropFormRecentlySubmitted');
    const resetAllForms = localStorage.getItem('resetAllForms');
    
    if (wasRecentlySubmitted || resetAllForms) {
      // Réinitialiser le formulaire de culture
      setCropData({});
      
      localStorage.removeItem('cropFormRecentlySubmitted');
      // Ne pas supprimer resetAllForms ici pour permettre aux autres composants de le lire
    }
  }, []);

  // Charger les types de cultures au début
  useEffect(() => {
    const getCropTypes = async () => {
      try {
        const response = await api.get('/croptype/list-crop');
        if (response && response.data && response.data.Croptype) {
          setListCrop(response.data.Croptype);
        }
      } catch (error) {
        console.error("Error loading crop types:", error);
      }
    };
    
    getCropTypes();
  }, []);

  // Mettre à jour les données de crop
  const handleCropChange = (newCropData) => {
    setCropData(newCropData);
  };

const areCropDatasEqual = (a, b) => {
  if (!a || !b) return false;
  // On compare seulement les champs pertinents d’insertion
  const pick = d => ({
    zone_uid: d.zone_uid || "",
    field_uid: d.field_uid || "",
    cropType: d.cropType || "",
    previous_type: d.previous_type || "",
    plantingDate: d.plantingDate || "",
    rootDepth: d.rootDepth || "",
    days: d.days || "",
    variety: d.variety || "",
    ruPratique: d.ruPratique || "",
    density: d.density || "",
    ecartInter: d.ecartInter || "",
    ecartIntra: d.ecartIntra || "",
    surface: d.surface || "",
    growingDate: d.growingDate || ""
  });
  return JSON.stringify(pick(a)) === JSON.stringify(pick(b));
};

const handleSubmit = (e) => {
  e.preventDefault();

  const savedData = wizardData?.step3?.data;
  const alreadyCompleted = !!wizardData?.step3?.isCompleted;
  const unchanged = alreadyCompleted && areCropDatasEqual(cropData, savedData);

  // Trouver le nom du crop type sélectionné
  let cropTypeName = "";
  if (cropData.cropType) {
    const cropTypeInfo = listCrop.find(crop => crop.id == cropData.cropType);
    if (cropTypeInfo) {
      cropTypeName = cropTypeInfo.crop;
    }
  }

  // Si déjà complété et inchangé: on saute l'API => juste next
  if (unchanged) {
    completeStep(3, {
      ...savedData,
      cropTypeId: savedData.cropType,
      cropTypeName: savedData.cropTypeName || cropTypeName
    });
    onNext();
    return;
  }

  // Si modifié ou première soumission: faire POST/UPDATE
  const apiData = {
    zone_uid: cropData.zone_uid || wizardData.zoneId,
    field_uid: cropData.field_uid || wizardData.fieldId,
    croptype_id: cropData.cropType,
    previous_type: cropData.previous_type || "",
    plantingDate: cropData.plantingDate || "",
    rootDepth: cropData.rootDepth || "",
    days: cropData.days || "",
    crop_variety_id: cropData.variety || "",
    practical_fraction: cropData.ruPratique || "",
    density: cropData.density || "",
    ecart_inter: cropData.ecartInter || "",
    ecart_intra: cropData.ecartIntra || "",
    surface: cropData.surface || "",
    growingDate: cropData.growingDate || "",
  };

  api.post('/crop/add-crop', apiData)
    .then(response => {
      if (response.data.type === "success") {
        localStorage.setItem('cropFormRecentlySubmitted', 'true');
        swal({
          title: t("crop_added_title"),
          text: t("crop_added_text"),
          icon: "success",
          buttons: [t("no"), t("yes")]
        }).then((willContinue) => {
          completeStep(3, {
            ...cropData,
            cropTypeId: cropData.cropType,
            cropTypeName: cropTypeName || cropData.cropName || ""
          });
          if (willContinue) {
            onNext();
          } else {
            const userId = getUserId();
            localStorage.removeItem(`smartFarmWizardData_${userId}`);
            localStorage.setItem('resetAllForms', 'true');
            navigate('/AddField');
          }
        });
      } else if (response.data.type === "warning") {
        swal({
          title: t("warning"),
          text: response.data.message,
          icon: "warning",
          button: t("ok")
        });
      } else {
        swal(t("Error"), t("Failed_to_add_crop_information"), "error");
      }
    })
    .catch(error => {
      console.error("API error:", error);

      // Le backend retourne status 200 avec type "warning" maintenant
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        if (errorData.type === "warning") {
          swal({
            title: t("warning"),
            text: errorData.message,
            icon: "warning",
            button: t("ok")
          });
        } else {
          swal(t("Error"), errorData.message || t("Failed_to_add_crop_information"), "error");
        }
      } else {
        swal(t("Error"), t("Failed_to_add_crop_information"), "error");
      }
    });
};

  return (
    <div className="wizard-step-content p-4">
      <Form onSubmit={handleSubmit}>
        <AddCropInfo
          isWizardMode={true}
          fieldId={wizardData.fieldId}
          fieldName={wizardData.fieldName}
          zoneId={wizardData.zoneId}
          zoneName={wizardData.zoneName}
          formData={cropData}
          onChange={handleCropChange}
          onSubmit={() => {}}
        />
        <div className="d-flex justify-content-between mt-4">
          <Button variant="outline-secondary" onClick={onPrevious} className="bg-white">
            <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
            {t('previous')}
          </Button>
          <Button type="submit">
            {wizardData?.step3?.isCompleted ? (
              <>
                {t('next')} <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
              </>
            ) : (
              t('submit')
            )}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddCropInfoWrapper;
